import type { Position, rangeLabel } from "../data/types";

/**
 * Returns true if a tile at squared-distance d² is in `range`.
 *  - Scalar label: disc bound (d² ≤ label²). Label 9 → d² ≤ 81 (effectively
 *    unbounded for current ~15-radius `deltas` table; full-board coverage
 *    would need a larger deltas table).
 *  - Array of labels: exact ring match — true only if d² == floor(label²)
 *    for any label. Each ring is a self-merge-safe set (cells in one ring
 *    are never 4-neighbor-adjacent to each other).
 */
export function inRange(d2: number, range: rangeLabel | rangeLabel[]): boolean {
  if (typeof range === "number") return d2 <= range * range;
  for (const label of range) {
    if (d2 === Math.floor(label * label)) return true;
  }
  return false;
}

/** Maximum d² covered by `range` — used to bound iteration / flood-fill. */
export function maxD2(range: rangeLabel | rangeLabel[]): number {
  if (typeof range === "number") return range * range;
  let max = 0;
  for (const label of range) {
    const d2 = Math.floor(label * label);
    if (d2 > max) max = d2;
  }
  return max;
}

/**
 * Deep equality comparison for JSON-serializable values
 * Uses JSON.stringify for comparison
 */
export function eq(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function unique<T>(list: T[]) {
  return [...new Set(list)];
}

/**
 * Generic flood fill algorithm
 * Finds all connected items starting from initial items
 * Always visits neighbors of start items,
 * but still checks start items with filter for adding
 */
export function floodFill<T>(
  start: T[],
  getNeighbors: (item: T) => T[],
  filter?: (item: T) => boolean,
): T[] {
  const visited = new Set<T>();
  const result: T[] = [];

  const queue: T[] = [...start, ...start.map((item) => getNeighbors(item)).flat()];

  for (let item = queue.shift(); item; item = queue.shift()) {
    if (visited.has(item)) continue;
    visited.add(item);
    if (filter && !filter(item)) continue;
    result.push(item);
    queue.push(...getNeighbors(item));
  }

  return result;
}

/** Unweighted centroid of a non-empty list of positions. */
export function centroidOf(positions: Position[]): Position {
  const x = positions.reduce((s, p) => s + p.x, 0) / positions.length;
  const y = positions.reduce((s, p) => s + p.y, 0) / positions.length;
  return { x, y };
}

/**
 * Prepare positions in range 15 from center (31x31) grid
 * Uses Euclidean distance √(x²+y²) for range check
 * Sorts by distance (closest first), then clockwise from right (east)
 */
function preparePositionDeltas() {
  const positions: (Position & { d2: number; deg: number })[] = [];
  for (let dy = -15; dy <= 15; dy++) {
    for (let dx = -15; dx <= 15; dx++) {
      const d2 = dx * dx + dy * dy;
      const angle = Math.atan2(dy, dx); // Clockwise in screen coords (Y down)
      let deg = Math.round((angle * 180) / Math.PI);
      if (deg < 0) deg += 360;
      if (deg === 0) deg = 0;
      if (d2 > 15 * 15) continue;
      positions.push({ x: dx, y: dy, d2: d2, deg: deg });
    }
  }
  return positions.sort((a, b) => {
    return a.d2 - b.d2 || a.deg - b.deg || b.d2 - a.d2;
  });
}

export const deltas = preparePositionDeltas();

/**
 * Seeded random number generator (mulberry32)
 * Returns a function that generates random numbers in [0, 1)
 */
export function seededRandom(seed: number): () => number {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Distributes damage across tiles using hemisphere (half-ball) formula
 * Damage falls off like the height of a dome: sqrt(radius² - distance²)
 *
 * @param tiles - Array of tiles with their positions and current shade
 * @param center - Center position of the explosion
 * @param totalDamage - Total damage to distribute
 * @param tileRangeRadius - Tile range radius (ball radius = tileRangeRadius + 0.5)
 * @returns Array of damage dealt to each tile (as integers, parallel to input)
 */
export function calculateHalfBallDamage(
  tiles: { position: Position; shade: number }[],
  center: Position,
  totalDamage: number,
  tileRangeRadius: number,
): number[] {
  if (tiles.length === 0) return [];

  // Ball radius is tile range radius + 0.5
  const ballRadius = tileRangeRadius + 0.5;
  const ballRadiusSquared = ballRadius * ballRadius;

  // Calculate hemisphere weights for each tile
  const weights = tiles.map((tile) => {
    const dx = tile.position.x - center.x;
    const dy = tile.position.y - center.y;
    const distanceSquared = dx * dx + dy * dy;

    // Hemisphere height: h = sqrt(R² - d²)
    const height = Math.sqrt(Math.max(0, ballRadiusSquared - distanceSquared));
    return height;
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight === 0) return tiles.map(() => 0);

  const maxDamagePerTile = Math.ceil((totalDamage / tiles.length) * 3);

  // Calculate raw damage for each tile (as floats)
  const rawDamages = weights.map((w) => totalDamage * (w / totalWeight));

  // Distribute damage as integers, capping at tile's current shade
  const result = tiles.map(() => 0);
  let remainingDamage = totalDamage;

  // First pass: assign floor damage, capping at shade
  const fractionalParts: { index: number; fraction: number }[] = [];
  tiles.forEach((tile, i) => {
    const floorDamage = Math.floor(rawDamages[i]);
    const cappedDamage = Math.min(floorDamage, tile.shade, maxDamagePerTile);

    result[i] = cappedDamage;
    remainingDamage -= cappedDamage;

    // Track fractional parts for rounding
    if (floorDamage < tile.shade && floorDamage < maxDamagePerTile) {
      fractionalParts.push({
        index: i,
        fraction: rawDamages[i] - floorDamage,
      });
    }
  });

  // Second pass: distribute remaining damage one point at a time based on fractional parts.
  fractionalParts.sort((a, b) => b.fraction - a.fraction);
  while (remainingDamage > 0 && fractionalParts.length > 0) {
    let distributedThisRound = 0;

    for (const { index } of fractionalParts) {
      if (remainingDamage <= 0) break;
      if (result[index] >= tiles[index].shade) continue;
      if (result[index] >= maxDamagePerTile) continue;

      result[index] += 1;
      remainingDamage -= 1;
      distributedThisRound += 1;
    }

    if (distributedThisRound === 0) break;
  }

  return result;
}
